/**-----------------------------------------------------------------------------------------
* Copyright © 2024 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import * as i0 from '@angular/core';
import { Component, Input, HostBinding, Injectable, Directive, EventEmitter, ViewChild, Output, ElementRef, HostListener, forwardRef, isDevMode, NgModule } from '@angular/core';
import * as i7 from '@angular/common';
import { CommonModule } from '@angular/common';
import { validatePackage } from '@progress/kendo-licensing';
import { currentPage, removeChildren, scrollToPage, calculateZoomLevel, SearchService, print, goToNextSearchMatch, goToPreviousSearchMatch, loadPDF, Scroller, reloadDocument } from '@progress/kendo-pdfviewer-common';
import * as i5$1 from '@progress/kendo-angular-common';
import { PreventableEvent, focusableSelector, Keys, hasObservers, shouldShowValidationUI, WatermarkModule } from '@progress/kendo-angular-common';
import * as i1$1 from '@progress/kendo-angular-l10n';
import { ComponentMessages, LocalizationService, L10N_PREFIX } from '@progress/kendo-angular-l10n';
import { saveAs } from '@progress/kendo-file-saver';
import { Subscription } from 'rxjs';
import 'pdfjs-dist/build/pdf.worker.entry';
import * as i1 from '@progress/kendo-angular-indicators';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { zoomInIcon, zoomOutIcon, handIcon, pointerIcon, searchIcon, folderOpenIcon, downloadIcon, printIcon, convertLowercaseIcon, arrowUpIcon, arrowDownIcon, xIcon } from '@progress/kendo-svg-icons';
import * as i4 from '@progress/kendo-angular-pager';
import { PagerModule } from '@progress/kendo-angular-pager';
import * as i5 from '@progress/kendo-angular-buttons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import * as i1$2 from '@progress/kendo-angular-dropdowns';
import { ComboBoxModule } from '@progress/kendo-angular-dropdowns';
import * as i2 from '@progress/kendo-angular-inputs';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';

/**
 * @hidden
 */
const _c0 = ["kendoPDFViewerLoader", ""];
const _c1 = ["*"];
const _c2 = ["fileSelectEl"];
const _c3 = ["kendoPDFViewerToolbar", ""];
function ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "kendo-toolbar-input-wrapper", 14)(1, "kendo-datapager", 15, 0);
    i0.ɵɵlistener("pageChange", function ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_2_Template_kendo_datapager_pageChange_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.pageChange.emit($event));
    });
    i0.ɵɵelement(3, "kendo-datapager-messages", 16);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const pager_r3 = i0.ɵɵreference(2);
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("toolbarTool", pager_r3);
    i0.ɵɵadvance();
    i0.ɵɵclassProp("k-disabled", ctx_r1.disabledTools);
    i0.ɵɵproperty("navigable", true)("pageSize", ctx_r1.pageSize)("skip", ctx_r1.skip)("total", ctx_r1.total)("pageSizeValues", false)("info", false)("type", ctx_r1.pagerType);
    i0.ɵɵattribute("aria-controls", ctx_r1.pagesContainerId)("aria-label", ctx_r1.messageFor("page") + " " + (ctx_r1.skip + 1) + " " + ctx_r1.messageFor("of") + ctx_r1.total);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("firstPage", ctx_r1.messageFor("pagerFirstPage"))("previousPage", ctx_r1.messageFor("pagerPreviousPage"))("nextPage", ctx_r1.messageFor("pagerNextPage"))("lastPage", ctx_r1.messageFor("pagerLastPage"))("inputLabel", ctx_r1.messageFor("pagerInputLabel"))("pageNumberInputTitle", ctx_r1.messageFor("pagerInputTitle"))("of", ctx_r1.messageFor("pagerOf"))("page", ctx_r1.messageFor("pagerPage"));
  }
}
function ToolbarComponent_ng_container_0_span_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 17);
  }
}
function ToolbarComponent_ng_container_0_kendo_buttongroup_4_Template(rf, ctx) {
  if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "kendo-buttongroup", 18)(1, "button", 19);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_kendo_buttongroup_4_Template_button_click_1_listener() {
      i0.ɵɵrestoreView(_r4);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.zoomOut.emit());
    });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "button", 20);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_kendo_buttongroup_4_Template_button_click_2_listener() {
      i0.ɵɵrestoreView(_r4);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.zoomIn.emit());
    });
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("navigable", false);
    i0.ɵɵattribute("aria-controls", ctx_r1.pagesContainerId);
    i0.ɵɵadvance();
    i0.ɵɵproperty("disabled", ctx_r1.disabledTools || ctx_r1.zoomOutDisabled)("svgIcon", ctx_r1.zoomOutIcon);
    i0.ɵɵattribute("title", ctx_r1.messageFor("zoomOutTitle"))("aria-label", ctx_r1.messageFor("zoomOutTitle"));
    i0.ɵɵadvance();
    i0.ɵɵproperty("disabled", ctx_r1.disabledTools || ctx_r1.zoomInDisabled)("svgIcon", ctx_r1.zoomInIcon);
    i0.ɵɵattribute("title", ctx_r1.messageFor("zoomInTitle"))("aria-label", ctx_r1.messageFor("zoomInTitle"));
  }
}
function ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_5_ng_template_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const dataItem_r6 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵtextInterpolate1(" ", dataItem_r6.type ? ctx_r1.messageFor("f" + dataItem_r6.type.slice(1)) : dataItem_r6.text, " ");
  }
}
function ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_5_Template(rf, ctx) {
  if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "kendo-toolbar-input-wrapper", 14)(1, "kendo-combobox", 21, 1);
    i0.ɵɵlistener("valueChange", function ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_5_Template_kendo_combobox_valueChange_1_listener($event) {
      i0.ɵɵrestoreView(_r5);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.onZoomLevelChooserValueChange($event));
    });
    i0.ɵɵtemplate(3, ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_5_ng_template_3_Template, 1, 1, "ng-template", 22);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const combobox_r7 = i0.ɵɵreference(2);
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("toolbarTool", combobox_r7);
    i0.ɵɵadvance();
    i0.ɵɵproperty("tabindex", -1)("data", ctx_r1.zoomOptionsData)("disabled", ctx_r1.disabledTools)("placeholder", ctx_r1.messageFor("zoomInputPlaceholder"))("value", ctx_r1.zoomLevelChooserValue)("allowCustom", true);
    i0.ɵɵattribute("aria-controls", ctx_r1.pagesContainerId);
  }
}
function ToolbarComponent_ng_container_0_kendo_buttongroup_6_Template(rf, ctx) {
  if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "kendo-buttongroup", 23)(1, "button", 24);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_kendo_buttongroup_6_Template_button_click_1_listener() {
      i0.ɵɵrestoreView(_r8);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.selectionEnabled.emit());
    });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "button", 25);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_kendo_buttongroup_6_Template_button_click_2_listener() {
      i0.ɵɵrestoreView(_r8);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.panningEnabled.emit());
    });
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("navigable", false);
    i0.ɵɵattribute("aria-controls", ctx_r1.pagesContainerId);
    i0.ɵɵadvance();
    i0.ɵɵproperty("toggleable", true)("svgIcon", ctx_r1.pointerIcon);
    i0.ɵɵattribute("title", ctx_r1.messageFor("selectionTitle"))("aria-label", ctx_r1.messageFor("selectionTitle"));
    i0.ɵɵadvance();
    i0.ɵɵproperty("svgIcon", ctx_r1.handIcon)("toggleable", true)("selected", true);
    i0.ɵɵattribute("title", ctx_r1.messageFor("panningTitle"))("aria-label", ctx_r1.messageFor("panningTitle"));
  }
}
function ToolbarComponent_ng_container_0_button_7_Template(rf, ctx) {
  if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 26);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_button_7_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r9);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.search.emit());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("disabled", ctx_r1.disabledTools)("svgIcon", ctx_r1.searchIcon);
    i0.ɵɵattribute("title", ctx_r1.messageFor("searchTitle"))("aria-label", ctx_r1.messageFor("searchTitle"));
  }
}
function ToolbarComponent_ng_container_0_button_8_Template(rf, ctx) {
  if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 27);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_button_8_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r10);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.fileSelectEl == null ? null : ctx_r1.fileSelectEl.nativeElement.click());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("svgIcon", ctx_r1.folderOpenIcon);
    i0.ɵɵattribute("title", ctx_r1.messageFor("openTitle"))("aria-label", ctx_r1.messageFor("openTitle"))("aria-controls", ctx_r1.pagesContainerId);
  }
}
function ToolbarComponent_ng_container_0_button_9_Template(rf, ctx) {
  if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 28);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_button_9_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r11);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.download.emit());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("svgIcon", ctx_r1.downloadIcon)("disabled", ctx_r1.disabledTools);
    i0.ɵɵattribute("title", ctx_r1.messageFor("downloadTitle"))("aria-label", ctx_r1.messageFor("downloadTitle"));
  }
}
function ToolbarComponent_ng_container_0_button_10_Template(rf, ctx) {
  if (rf & 1) {
    const _r12 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 29);
    i0.ɵɵlistener("click", function ToolbarComponent_ng_container_0_button_10_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r12);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.print.emit());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("svgIcon", ctx_r1.printIcon)("disabled", ctx_r1.disabledTools);
    i0.ɵɵattribute("title", ctx_r1.messageFor("printTitle"))("aria-label", ctx_r1.messageFor("printTitle"));
  }
}
function ToolbarComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0)(1, 5);
    i0.ɵɵtemplate(2, ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_2_Template, 4, 20, "kendo-toolbar-input-wrapper", 6)(3, ToolbarComponent_ng_container_0_span_3_Template, 1, 0, "span", 7)(4, ToolbarComponent_ng_container_0_kendo_buttongroup_4_Template, 3, 10, "kendo-buttongroup", 8)(5, ToolbarComponent_ng_container_0_kendo_toolbar_input_wrapper_5_Template, 4, 8, "kendo-toolbar-input-wrapper", 6)(6, ToolbarComponent_ng_container_0_kendo_buttongroup_6_Template, 3, 11, "kendo-buttongroup", 9)(7, ToolbarComponent_ng_container_0_button_7_Template, 1, 4, "button", 10)(8, ToolbarComponent_ng_container_0_button_8_Template, 1, 4, "button", 11)(9, ToolbarComponent_ng_container_0_button_9_Template, 1, 4, "button", 12)(10, ToolbarComponent_ng_container_0_button_10_Template, 1, 4, "button", 13);
    i0.ɵɵelementContainerEnd()();
  }
  if (rf & 2) {
    const tool_r13 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitch", tool_r13);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "pager");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "spacer");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "zoomInOut");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "zoom");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "selection");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "search");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "open");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "download");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "print");
  }
}
function ToolbarComponent_input_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r14 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "input", 30, 2);
    i0.ɵɵlistener("change", function ToolbarComponent_input_1_Template_input_change_0_listener($event) {
      i0.ɵɵrestoreView(_r14);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onFileSelect($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵstyleProp("display", "none");
  }
}
const _c4 = ["textbox"];
const _c5 = ["closeButton"];
const _c6 = ["kendoPDFViewerSearch", ""];
function PDFViewerSearchComponent_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 8);
    i0.ɵɵlistener("click", function PDFViewerSearchComponent_ng_template_2_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r2);
      const ctx_r2 = i0.ɵɵnextContext();
      ctx_r2.matchCase = !ctx_r2.matchCase;
      return i0.ɵɵresetView(ctx_r2.searchChange.emit({
        text: ctx_r2.value,
        matchCase: ctx_r2.matchCase
      }));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("togglable", true)("svgIcon", ctx_r2.convertLowercaseIcon);
    i0.ɵɵattribute("title", ctx_r2.messageFor("searchMatchCaseTitle"));
  }
}
const _c7 = ["pagesContainer"];
function PDFViewerComponent_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 11);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("settings", ctx_r1.loaderSettings);
  }
}
function PDFViewerComponent_div_5_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 12);
    i0.ɵɵlistener("searchChange", function PDFViewerComponent_div_5_Template_div_searchChange_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onSearchChange($event));
    })("prevMatch", function PDFViewerComponent_div_5_Template_div_prevMatch_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onPreviousMatch());
    })("nextMatch", function PDFViewerComponent_div_5_Template_div_nextMatch_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onNextMatch());
    })("close", function PDFViewerComponent_div_5_Template_div_close_0_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext();
      const toolbar_r4 = i0.ɵɵreference(3);
      return i0.ɵɵresetView(ctx_r1.closeSearch(toolbar_r4));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("currentMatch", ctx_r1.currentMatch)("matches", ctx_r1.currentMatches.length);
  }
}
function PDFViewerComponent_div_10_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 13);
  }
}
const packageMetadata = {
  name: '@progress/kendo-angular-pdfviewer',
  productName: 'Kendo UI for Angular',
  productCodes: ['KENDOUIANGULAR', 'KENDOUICOMPLETE'],
  publishDate: 1713437590,
  version: '15.5.0',
  licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/?utm_medium=product&utm_source=kendoangular&utm_campaign=kendo-ui-angular-purchase-license-keys-warning'
};

/**
 * @hidden
 */
const INITIAL_ZOOM_LEVEL = 1.25;
/**
 * @hidden
 */
const MIN_ZOOM_LEVEL = 0.5;
/**
 * @hidden
 */
const MAX_ZOOM_LEVEL = 4;
/**
 * @hidden
 */
const zoomOptionsData = [{
  id: 1,
  text: 'Fit to width',
  type: 'FitToWidth',
  displayValue: '',
  value: 1
}, {
  id: 2,
  text: 'Fit to page',
  type: 'FitToPage',
  displayValue: '',
  value: 1
}, {
  id: 3,
  text: '50%',
  displayValue: '50%',
  value: 0.5
}, {
  id: 4,
  text: '75%',
  displayValue: '75%',
  value: 0.75
}, {
  id: 5,
  text: '100%',
  displayValue: '100%',
  value: 1
}, {
  id: 6,
  text: '125%',
  displayValue: '125%',
  value: 1.25
}, {
  id: 7,
  text: '150%',
  displayValue: '150%',
  value: 1.5
}, {
  id: 8,
  text: '200%',
  displayValue: '200%',
  value: 2
}, {
  id: 9,
  text: '300%',
  displayValue: '300%',
  value: 3
}, {
  id: 10,
  text: '400%',
  displayValue: '400%',
  value: 4
}];

/**
 * Arguments for the `download` event. The event fires when the end user clicks the Download tool.
 * If you cancel the event, the downloading of the file is prevented.
 */
class PDFViewerDownloadEvent extends PreventableEvent {
  /**
   * @hidden
   */
  constructor(blob, fileName, saveOptions, context) {
    super();
    this.blob = blob;
    this.fileName = fileName;
    this.saveOptions = saveOptions;
    this.context = context;
  }
}

/**
 * @hidden
 */
class LoaderComponent {
  constructor() {
    this.loaderContainerClass = true;
    this.loaderContainerSizingClass = true;
    this.loaderPositionClass = true;
  }
}
LoaderComponent.ɵfac = function LoaderComponent_Factory(t) {
  return new (t || LoaderComponent)();
};
LoaderComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: LoaderComponent,
  selectors: [["", "kendoPDFViewerLoader", ""]],
  hostVars: 6,
  hostBindings: function LoaderComponent_HostBindings(rf, ctx) {
    if (rf & 2) {
      i0.ɵɵclassProp("k-loader-container", ctx.loaderContainerClass)("k-loader-container-md", ctx.loaderContainerSizingClass)("k-loader-top", ctx.loaderPositionClass);
    }
  },
  inputs: {
    settings: "settings"
  },
  attrs: _c0,
  decls: 3,
  vars: 3,
  consts: [[1, "k-loader-container-overlay", "k-overlay-light"], [1, "k-loader-container-inner"], [3, "size", "type", "themeColor"]],
  template: function LoaderComponent_Template(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵelement(0, "div", 0);
      i0.ɵɵelementStart(1, "div", 1);
      i0.ɵɵelement(2, "kendo-loader", 2);
      i0.ɵɵelementEnd();
    }
    if (rf & 2) {
      i0.ɵɵadvance(2);
      i0.ɵɵproperty("size", ctx.settings == null ? null : ctx.settings.size)("type", ctx.settings == null ? null : ctx.settings.type)("themeColor", ctx.settings == null ? null : ctx.settings.themeColor);
    }
  },
  dependencies: [i1.LoaderComponent],
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoaderComponent, [{
    type: Component,
    args: [{
      selector: '[kendoPDFViewerLoader]',
      template: `
        <div class="k-loader-container-overlay k-overlay-light"></div>
        <div class="k-loader-container-inner">
            <kendo-loader
                [size]="settings?.size"
                [type]="settings?.type"
                [themeColor]="settings?.themeColor"></kendo-loader>
        </div>
    `
    }]
  }], null, {
    settings: [{
      type: Input
    }],
    loaderContainerClass: [{
      type: HostBinding,
      args: ['class.k-loader-container']
    }],
    loaderContainerSizingClass: [{
      type: HostBinding,
      args: ['class.k-loader-container-md']
    }],
    loaderPositionClass: [{
      type: HostBinding,
      args: ['class.k-loader-top']
    }]
  });
})();

/**
 * @hidden
 */
class ToolbarNavigationService {
  constructor(localizationService) {
    this.localizationService = localizationService;
    this.focusableTools = [];
    this.currentFocusIndex = 0;
  }
  register(tool) {
    if (!this.focusableTools.some(el => el === tool)) {
      this.focusableTools.push(tool);
    }
  }
  unregister(tool) {
    this.currentFocusIndex = 0;
    this.focusableTools = this.focusableTools.filter(el => el !== tool);
  }
  isActive(focusable) {
    return this.focusableTools[this.currentFocusIndex] === focusable;
  }
  setActiveIndex(tool) {
    this.currentFocusIndex = Math.max(this.focusableTools.indexOf(tool), 0);
  }
  move(direction) {
    let delta = direction === 'right' ? 1 : -1;
    if (this.localizationService.rtl) {
      delta = -delta;
    }
    this.currentFocusIndex += delta;
    if (this.currentFocusIndex < 0) {
      this.currentFocusIndex = this.focusableTools.length - 1;
    } else if (this.currentFocusIndex >= this.focusableTools.length) {
      this.currentFocusIndex = 0;
    }
    this.focusableTools[this.currentFocusIndex].activate();
  }
}
ToolbarNavigationService.ɵfac = function ToolbarNavigationService_Factory(t) {
  return new (t || ToolbarNavigationService)(i0.ɵɵinject(i1$1.LocalizationService));
};
ToolbarNavigationService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
  token: ToolbarNavigationService,
  factory: ToolbarNavigationService.ɵfac
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToolbarNavigationService, [{
    type: Injectable
  }], function () {
    return [{
      type: i1$1.LocalizationService
    }];
  }, null);
})();

/**
 * @hidden
 */
class ToolbarInputWrapperComponent {
  constructor(host, renderer) {
    this.host = host;
    this.renderer = renderer;
    this.hostClass = true;
    this.subs = new Subscription();
  }
  ngAfterViewInit() {
    this.subs.add(this.renderer.listen(this.host.nativeElement, 'keydown.enter', () => {
      if (this.toolbarTool.focus) {
        this.toolbarTool.focus();
      } else if (this.toolbarTool.element) {
        this.toolbarTool.element.nativeElement.setAttribute('tabindex', '0');
        this.toolbarTool.element.nativeElement.focus();
      }
    }));
    this.subs.add(this.renderer.listen(this.host.nativeElement, 'blur', () => this.host.nativeElement.querySelectorAll(focusableSelector).forEach(el => el.setAttribute('tabindex', '-1'))));
    this.subs.add(this.renderer.listen(this.host.nativeElement, 'keydown.escape', () => {
      if (this.toolbarTool.blur) {
        this.toolbarTool.blur();
      } else if (this.toolbarTool.element) {
        this.toolbarTool.element.nativeElement.setAttribute('tabindex', '-1');
        this.toolbarTool.element.nativeElement.blur();
      }
      ;
      this.host.nativeElement.focus();
    }));
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
ToolbarInputWrapperComponent.ɵfac = function ToolbarInputWrapperComponent_Factory(t) {
  return new (t || ToolbarInputWrapperComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
};
ToolbarInputWrapperComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: ToolbarInputWrapperComponent,
  selectors: [["kendo-toolbar-input-wrapper"]],
  hostVars: 2,
  hostBindings: function ToolbarInputWrapperComponent_HostBindings(rf, ctx) {
    if (rf & 2) {
      i0.ɵɵclassProp("k-toolbar-item", ctx.hostClass);
    }
  },
  inputs: {
    toolbarTool: "toolbarTool"
  },
  ngContentSelectors: _c1,
  decls: 1,
  vars: 0,
  template: function ToolbarInputWrapperComponent_Template(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵprojectionDef();
      i0.ɵɵprojection(0);
    }
  },
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToolbarInputWrapperComponent, [{
    type: Component,
    args: [{
      selector: 'kendo-toolbar-input-wrapper',
      template: `
        <ng-content></ng-content>
    `
    }]
  }], function () {
    return [{
      type: i0.ElementRef
    }, {
      type: i0.Renderer2
    }];
  }, {
    toolbarTool: [{
      type: Input
    }],
    hostClass: [{
      type: HostBinding,
      args: ['class.k-toolbar-item']
    }]
  });
})();
const controller = new AbortController();
const {
  signal
} = controller;
/**
 * @hidden
 */
class ToolbarFocusableDirective {
  constructor(host, navigationService, renderer) {
    this.host = host;
    this.navigationService = navigationService;
    this.renderer = renderer;
    navigationService.register(this);
  }
  ngAfterViewInit() {
    const element = this.host.nativeElement;
    this.renderer.setAttribute(element, 'tabindex', this.navigationService.isActive(this) ? '0' : '-1');
    element.addEventListener('keydown', e => {
      const targetsSelf = e.target === element;
      const isLeftArrow = e.keyCode === Keys.ArrowLeft;
      const isRightArrow = e.keyCode === Keys.ArrowRight;
      const isArrow = isLeftArrow || isRightArrow;
      if (!targetsSelf || !isArrow) {
        return;
      }
      this.renderer.setAttribute(element, 'tabindex', '-1');
      element.querySelectorAll(focusableSelector).forEach(el => {
        this.renderer.setAttribute(el, 'tabindex', '-1');
      });
      if (isRightArrow) {
        this.navigationService.move('right');
      } else if (isLeftArrow) {
        this.navigationService.move('left');
      }
    }, {
      signal,
      capture: true
    });
    element.addEventListener('click', () => {
      this.navigationService.setActiveIndex(this);
    }, {
      signal,
      capture: true
    });
  }
  ngOnDestroy() {
    controller.abort();
    this.navigationService.unregister(this);
  }
  activate() {
    const element = this.host.nativeElement;
    this.renderer.setAttribute(element, 'tabindex', '0');
    element.focus();
  }
}
ToolbarFocusableDirective.ɵfac = function ToolbarFocusableDirective_Factory(t) {
  return new (t || ToolbarFocusableDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(ToolbarNavigationService), i0.ɵɵdirectiveInject(i0.Renderer2));
};
ToolbarFocusableDirective.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
  type: ToolbarFocusableDirective,
  selectors: [["", "kendoPDFViewerToolbarFocusable", ""]]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToolbarFocusableDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoPDFViewerToolbarFocusable]'
    }]
  }], function () {
    return [{
      type: i0.ElementRef
    }, {
      type: ToolbarNavigationService
    }, {
      type: i0.Renderer2
    }];
  }, null);
})();

/**
 * @hidden
 */
class ToolbarComboBoxDirective {
  constructor(combo, hostEl) {
    this.combo = combo;
    this.hostEl = hostEl;
    this.keydownHandler = e => {
      if (e.keyCode === Keys.Escape) {
        e.stopPropagation();
        if (this.combo.isOpen) {
          this.combo.toggle(false);
        } else {
          this.hostEl.nativeElement.parentElement.focus();
        }
      }
    };
  }
  ngAfterViewInit() {
    this.inputElement = this.combo.searchbar.input.nativeElement;
    this.hostEl.nativeElement.setAttribute('tabindex', '-1');
    this.inputElement.addEventListener('keydown', this.keydownHandler, true);
  }
  ngOnDestroy() {
    this.inputElement.removeEventListener('keydown', this.keydownHandler);
  }
}
ToolbarComboBoxDirective.ɵfac = function ToolbarComboBoxDirective_Factory(t) {
  return new (t || ToolbarComboBoxDirective)(i0.ɵɵdirectiveInject(i1$2.ComboBoxComponent), i0.ɵɵdirectiveInject(i0.ElementRef));
};
ToolbarComboBoxDirective.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
  type: ToolbarComboBoxDirective,
  selectors: [["", "kendoPDFViewerComboBox", ""]]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToolbarComboBoxDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoPDFViewerComboBox]'
    }]
  }], function () {
    return [{
      type: i1$2.ComboBoxComponent
    }, {
      type: i0.ElementRef
    }];
  }, null);
})();

/* eslint-disable @typescript-eslint/no-empty-function */
/**
 * @hidden
 */
class ToolbarComponent {
  constructor(localization, navigationService) {
    this.localization = localization;
    this.navigationService = navigationService;
    this.toolbarClass = true;
    this.toolbarSizeClass = true;
    this.zoomLevel = {
      text: '125%',
      displayValue: '125%',
      value: 1.25
    };
    this.calculatedComboBoxValue = {
      text: 'Fit to width',
      displayValue: null,
      value: null
    };
    this.skip = 0;
    this.pageSize = 1;
    this.fileSelect = new EventEmitter();
    this.fileSelectStart = new EventEmitter();
    this.fileSelectError = new EventEmitter();
    this.download = new EventEmitter();
    this.selectionEnabled = new EventEmitter();
    this.panningEnabled = new EventEmitter();
    this.pageChange = new EventEmitter();
    this.zoomIn = new EventEmitter();
    this.zoomOut = new EventEmitter();
    this.zoomLevelChange = new EventEmitter();
    this.print = new EventEmitter();
    this.search = new EventEmitter();
    this.zoomInIcon = zoomInIcon;
    this.zoomOutIcon = zoomOutIcon;
    this.handIcon = handIcon;
    this.pointerIcon = pointerIcon;
    this.searchIcon = searchIcon;
    this.folderOpenIcon = folderOpenIcon;
    this.downloadIcon = downloadIcon;
    this.printIcon = printIcon;
    this.pagerType = 'input';
  }
  messageFor(key) {
    return this.localization.get(key);
  }
  onFileSelect(e) {
    const file = e.target.files[0];
    this.fileSelectStart.emit();
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const data = e.target.result;
        this.fileSelect.emit(data);
      };
      reader.onerror = e => {
        this.fileSelectError.emit(e);
      };
      reader.readAsDataURL(file);
    }
  }
  onZoomLevelChooserValueChange(zoomLevel) {
    this.zoomLevelChange.emit(zoomLevel);
  }
  focus() {
    this.navigationService.focusableTools[this.navigationService.currentFocusIndex].activate();
  }
}
ToolbarComponent.ɵfac = function ToolbarComponent_Factory(t) {
  return new (t || ToolbarComponent)(i0.ɵɵdirectiveInject(i1$1.LocalizationService), i0.ɵɵdirectiveInject(ToolbarNavigationService));
};
ToolbarComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: ToolbarComponent,
  selectors: [["", "kendoPDFViewerToolbar", ""]],
  viewQuery: function ToolbarComponent_Query(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵviewQuery(_c2, 5);
    }
    if (rf & 2) {
      let _t;
      i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.fileSelectEl = _t.first);
    }
  },
  hostVars: 4,
  hostBindings: function ToolbarComponent_HostBindings(rf, ctx) {
    if (rf & 2) {
      i0.ɵɵclassProp("k-toolbar", ctx.toolbarClass)("k-toolbar-md", ctx.toolbarSizeClass);
    }
  },
  inputs: {
    zoomLevel: "zoomLevel",
    calculatedComboBoxValue: "calculatedComboBoxValue",
    skip: "skip",
    pageSize: "pageSize",
    total: "total",
    zoomInDisabled: "zoomInDisabled",
    zoomOutDisabled: "zoomOutDisabled",
    disabledTools: "disabledTools",
    zoomLevelChooserValue: "zoomLevelChooserValue",
    zoomOptionsData: "zoomOptionsData",
    pagesContainerId: "pagesContainerId",
    tools: "tools"
  },
  outputs: {
    fileSelect: "fileSelect",
    fileSelectStart: "fileSelectStart",
    fileSelectError: "fileSelectError",
    download: "download",
    selectionEnabled: "selectionEnabled",
    panningEnabled: "panningEnabled",
    pageChange: "pageChange",
    zoomIn: "zoomIn",
    zoomOut: "zoomOut",
    zoomLevelChange: "zoomLevelChange",
    print: "print",
    search: "search"
  },
  features: [i0.ɵɵProvidersFeature([ToolbarNavigationService])],
  attrs: _c3,
  decls: 2,
  vars: 2,
  consts: [["pager", ""], ["combobox", ""], ["fileSelectEl", ""], [4, "ngFor", "ngForOf"], ["type", "file", "aria-hidden", "true", "accept", ".pdf, .PDF", 3, "display", "change", 4, "ngIf"], [3, "ngSwitch"], ["kendoPDFViewerToolbarFocusable", "", 3, "toolbarTool", 4, "ngSwitchCase"], ["class", "k-spacer", 4, "ngSwitchCase"], [3, "navigable", 4, "ngSwitchCase"], ["selection", "single", 3, "navigable", 4, "ngSwitchCase"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "search", "aria-haspopup", "dialog", 3, "disabled", "svgIcon", "click", 4, "ngSwitchCase"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "folder-open", 3, "svgIcon", "click", 4, "ngSwitchCase"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "download", 3, "svgIcon", "disabled", "click", 4, "ngSwitchCase"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "print", 3, "svgIcon", "disabled", "click", 4, "ngSwitchCase"], ["kendoPDFViewerToolbarFocusable", "", 3, "toolbarTool"], [3, "pageChange", "navigable", "pageSize", "skip", "total", "pageSizeValues", "info", "type"], [3, "firstPage", "previousPage", "nextPage", "lastPage", "inputLabel", "pageNumberInputTitle", "of", "page"], [1, "k-spacer"], [3, "navigable"], ["type", "button", "kendoButton", "", "kendoPDFViewerToolbarFocusable", "", "icon", "zoom-out", 3, "click", "disabled", "svgIcon"], ["type", "button", "kendoButton", "", "kendoPDFViewerToolbarFocusable", "", "icon", "zoom-in", 3, "click", "disabled", "svgIcon"], ["kendoPDFViewerComboBox", "", "textField", "displayValue", "valueField", "id", 3, "valueChange", "tabindex", "data", "disabled", "placeholder", "value", "allowCustom"], ["kendoComboBoxItemTemplate", ""], ["selection", "single", 3, "navigable"], ["type", "button", "kendoButton", "", "kendoPDFViewerToolbarFocusable", "", "icon", "pointer", 3, "click", "toggleable", "svgIcon"], ["type", "button", "kendoButton", "", "kendoPDFViewerToolbarFocusable", "", "icon", "hand", 3, "click", "svgIcon", "toggleable", "selected"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "search", "aria-haspopup", "dialog", 3, "click", "disabled", "svgIcon"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "folder-open", 3, "click", "svgIcon"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "download", 3, "click", "svgIcon", "disabled"], ["type", "button", "kendoPDFViewerToolbarFocusable", "", "kendoButton", "", "icon", "print", 3, "click", "svgIcon", "disabled"], ["type", "file", "aria-hidden", "true", "accept", ".pdf, .PDF", 3, "change"]],
  template: function ToolbarComponent_Template(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵtemplate(0, ToolbarComponent_ng_container_0_Template, 11, 10, "ng-container", 3)(1, ToolbarComponent_input_1_Template, 2, 2, "input", 4);
    }
    if (rf & 2) {
      i0.ɵɵproperty("ngForOf", ctx.tools);
      i0.ɵɵadvance();
      i0.ɵɵproperty("ngIf", (ctx.tools == null ? null : ctx.tools.indexOf("open")) > -1);
    }
  },
  dependencies: [ToolbarInputWrapperComponent, i4.PagerComponent, i4.CustomMessagesComponent, i5.ButtonGroupComponent, i5.ButtonComponent, i1$2.ComboBoxComponent, i7.NgForOf, i7.NgSwitch, i7.NgSwitchCase, ToolbarFocusableDirective, ToolbarComboBoxDirective, i1$2.ItemTemplateDirective, i7.NgIf],
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ToolbarComponent, [{
    type: Component,
    args: [{
      selector: '[kendoPDFViewerToolbar]',
      providers: [ToolbarNavigationService],
      template: `
        <ng-container *ngFor="let tool of tools">
            <ng-container [ngSwitch]="tool">
                <kendo-toolbar-input-wrapper
                    *ngSwitchCase="'pager'"
                    kendoPDFViewerToolbarFocusable
                    [toolbarTool]="pager">
                    <kendo-datapager
                        #pager
                        [attr.aria-controls]="pagesContainerId"
                        [attr.aria-label]="messageFor('page') + ' ' + (skip + 1) + ' ' + messageFor('of') + total"
                        [class.k-disabled]="disabledTools"
                        [navigable]="true"
                        [pageSize]="pageSize"
                        [skip]="skip"
                        [total]="total"
                        [pageSizeValues]="false"
                        [info]="false"
                        [type]="pagerType"
                        (pageChange)="pageChange.emit($event)">
                        <kendo-datapager-messages
                            [firstPage]="messageFor('pagerFirstPage')"
                            [previousPage]="messageFor('pagerPreviousPage')"
                            [nextPage]="messageFor('pagerNextPage')"
                            [lastPage]="messageFor('pagerLastPage')"
                            [inputLabel]="messageFor('pagerInputLabel')"
                            [pageNumberInputTitle]="messageFor('pagerInputTitle')"
                            [of]="messageFor('pagerOf')"
                            [page]="messageFor('pagerPage')"></kendo-datapager-messages>
                    </kendo-datapager>
                </kendo-toolbar-input-wrapper>
                <span
                    *ngSwitchCase="'spacer'"
                    class="k-spacer"></span>
                <kendo-buttongroup
                    *ngSwitchCase="'zoomInOut'"
                    [attr.aria-controls]="pagesContainerId"
                    [navigable]="false">
                    <button
                        type="button"
                        kendoButton
                        kendoPDFViewerToolbarFocusable
                        icon="zoom-out"
                        [disabled]="disabledTools || zoomOutDisabled"
                        [svgIcon]="zoomOutIcon"
                        [attr.title]="messageFor('zoomOutTitle')"
                        [attr.aria-label]="messageFor('zoomOutTitle')"
                        (click)="zoomOut.emit()"></button>
                    <button
                        type="button"
                        kendoButton
                        kendoPDFViewerToolbarFocusable
                        icon="zoom-in"
                        [disabled]="disabledTools || zoomInDisabled"
                        [svgIcon]="zoomInIcon"
                        [attr.title]="messageFor('zoomInTitle')"
                        [attr.aria-label]="messageFor('zoomInTitle')"
                        (click)="zoomIn.emit()"></button>
                </kendo-buttongroup>
                <kendo-toolbar-input-wrapper
                    *ngSwitchCase="'zoom'"
                    kendoPDFViewerToolbarFocusable
                    [toolbarTool]="combobox">
                    <kendo-combobox
                        #combobox
                        kendoPDFViewerComboBox
                        [attr.aria-controls]="pagesContainerId"
                        [tabindex]="-1"
                        [data]="zoomOptionsData"
                        [disabled]="disabledTools"
                        [placeholder]="messageFor('zoomInputPlaceholder')"
                        textField="displayValue"
                        valueField="id"
                        [value]="zoomLevelChooserValue"
                        [allowCustom]="true"
                        (valueChange)="onZoomLevelChooserValueChange($event)">
                        <ng-template kendoComboBoxItemTemplate let-dataItem>
                            {{ dataItem.type ? messageFor('f' + dataItem.type.slice(1)) : dataItem.text }}
                        </ng-template>
                    </kendo-combobox>
                </kendo-toolbar-input-wrapper>
                <kendo-buttongroup
                    *ngSwitchCase="'selection'"
                    [attr.aria-controls]="pagesContainerId"
                    [navigable]="false"
                    selection="single">
                    <button
                        type="button"
                        kendoButton
                        kendoPDFViewerToolbarFocusable
                        icon="pointer"
                        [toggleable]="true"
                        [svgIcon]="pointerIcon"
                        [attr.title]="messageFor('selectionTitle')"
                        [attr.aria-label]="messageFor('selectionTitle')"
                        (click)="selectionEnabled.emit()"></button>
                    <button
                        type="button"
                        kendoButton
                        kendoPDFViewerToolbarFocusable
                        icon="hand"
                        [svgIcon]="handIcon"
                        [toggleable]="true"
                        [selected]="true"
                        [attr.title]="messageFor('panningTitle')"
                        [attr.aria-label]="messageFor('panningTitle')"
                        (click)="panningEnabled.emit()"></button>
                </kendo-buttongroup>
                <button
                    *ngSwitchCase="'search'"
                    type="button"
                    kendoPDFViewerToolbarFocusable
                    kendoButton
                    icon="search"
                    [disabled]="disabledTools"
                    [attr.title]="messageFor('searchTitle')"
                    [attr.aria-label]="messageFor('searchTitle')"
                    aria-haspopup="dialog"
                    [svgIcon]="searchIcon"
                    (click)="search.emit()"></button>
                <button
                    *ngSwitchCase="'open'"
                    type="button"
                    kendoPDFViewerToolbarFocusable
                    kendoButton
                    icon="folder-open"
                    [svgIcon]="folderOpenIcon"
                    [attr.title]="messageFor('openTitle')"
                    [attr.aria-label]="messageFor('openTitle')"
                    [attr.aria-controls]="pagesContainerId"
                    (click)="fileSelectEl?.nativeElement.click()">
                </button>
                <button
                    *ngSwitchCase="'download'"
                    type="button"
                    kendoPDFViewerToolbarFocusable
                    kendoButton
                    icon="download"
                    [svgIcon]="downloadIcon"
                    [disabled]="disabledTools"
                    [attr.title]="messageFor('downloadTitle')"
                    [attr.aria-label]="messageFor('downloadTitle')"
                    (click)="download.emit()"></button>
                <button
                    *ngSwitchCase="'print'"
                    type="button"
                    kendoPDFViewerToolbarFocusable
                    kendoButton
                    icon="print"
                    [svgIcon]="printIcon"
                    [disabled]="disabledTools"
                    [attr.title]="messageFor('printTitle')"
                    [attr.aria-label]="messageFor('printTitle')"
                    (click)="print.emit()"></button>
            </ng-container>
        </ng-container>
        <input
            #fileSelectEl
            *ngIf="tools?.indexOf('open') > -1;"
            type="file"
            [style.display]="'none'"
            aria-hidden="true"
            accept=".pdf, .PDF"
            (change)="onFileSelect($event)" />
    `
    }]
  }], function () {
    return [{
      type: i1$1.LocalizationService
    }, {
      type: ToolbarNavigationService
    }];
  }, {
    toolbarClass: [{
      type: HostBinding,
      args: ['class.k-toolbar']
    }],
    toolbarSizeClass: [{
      type: HostBinding,
      args: ['class.k-toolbar-md']
    }],
    fileSelectEl: [{
      type: ViewChild,
      args: ['fileSelectEl']
    }],
    zoomLevel: [{
      type: Input
    }],
    calculatedComboBoxValue: [{
      type: Input
    }],
    skip: [{
      type: Input
    }],
    pageSize: [{
      type: Input
    }],
    total: [{
      type: Input
    }],
    zoomInDisabled: [{
      type: Input
    }],
    zoomOutDisabled: [{
      type: Input
    }],
    disabledTools: [{
      type: Input
    }],
    zoomLevelChooserValue: [{
      type: Input
    }],
    zoomOptionsData: [{
      type: Input
    }],
    pagesContainerId: [{
      type: Input
    }],
    tools: [{
      type: Input
    }],
    fileSelect: [{
      type: Output
    }],
    fileSelectStart: [{
      type: Output
    }],
    fileSelectError: [{
      type: Output
    }],
    download: [{
      type: Output
    }],
    selectionEnabled: [{
      type: Output
    }],
    panningEnabled: [{
      type: Output
    }],
    pageChange: [{
      type: Output
    }],
    zoomIn: [{
      type: Output
    }],
    zoomOut: [{
      type: Output
    }],
    zoomLevelChange: [{
      type: Output
    }],
    print: [{
      type: Output
    }],
    search: [{
      type: Output
    }]
  });
})();

/**
 * @hidden
 */
class PDFViewerSearchComponent {
  constructor(localization) {
    this.localization = localization;
    this.ariaRole = 'dialog';
    this.searchChange = new EventEmitter();
    this.prevMatch = new EventEmitter();
    this.nextMatch = new EventEmitter();
    this.close = new EventEmitter();
    this.convertLowercaseIcon = convertLowercaseIcon;
    this.arrowUpIcon = arrowUpIcon;
    this.arrowDownIcon = arrowDownIcon;
    this.xIcon = xIcon;
    this.value = null;
    this.matchCase = false;
  }
  onEscape() {
    this.close.emit();
  }
  ngAfterViewInit() {
    this.textbox.focus();
  }
  messageFor(key) {
    return this.localization.get(key);
  }
  onShiftTab(e) {
    if (e.target.matches('.k-input-inner')) {
      e.preventDefault();
      this.closeButton.nativeElement.focus();
    }
  }
}
PDFViewerSearchComponent.ɵfac = function PDFViewerSearchComponent_Factory(t) {
  return new (t || PDFViewerSearchComponent)(i0.ɵɵdirectiveInject(i1$1.LocalizationService));
};
PDFViewerSearchComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: PDFViewerSearchComponent,
  selectors: [["", "kendoPDFViewerSearch", ""]],
  viewQuery: function PDFViewerSearchComponent_Query(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵviewQuery(_c4, 5);
      i0.ɵɵviewQuery(_c5, 5, ElementRef);
    }
    if (rf & 2) {
      let _t;
      i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.textbox = _t.first);
      i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.closeButton = _t.first);
    }
  },
  hostVars: 1,
  hostBindings: function PDFViewerSearchComponent_HostBindings(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵlistener("keydown.escape", function PDFViewerSearchComponent_keydown_escape_HostBindingHandler() {
        return ctx.onEscape();
      });
    }
    if (rf & 2) {
      i0.ɵɵattribute("role", ctx.ariaRole);
    }
  },
  inputs: {
    matches: "matches",
    currentMatch: "currentMatch"
  },
  outputs: {
    searchChange: "searchChange",
    prevMatch: "prevMatch",
    nextMatch: "nextMatch",
    close: "close"
  },
  attrs: _c6,
  decls: 13,
  vars: 13,
  consts: [["textbox", ""], ["closeButton", ""], [3, "valueChange", "keydown.shift.tab", "placeholder", "value"], ["kendoTextBoxSuffixTemplate", ""], [1, "k-search-matches"], ["type", "button", "kendoButton", "", "fillMode", "flat", "icon", "arrow-up", 3, "click", "disabled", "svgIcon"], ["type", "button", "kendoButton", "", "fillMode", "flat", "icon", "arrow-down", 3, "click", "disabled", "svgIcon"], ["type", "button", "kendoButton", "", "fillMode", "flat", "icon", "x", 3, "click", "keydown.tab", "svgIcon"], ["type", "button", "kendoButton", "", "fillMode", "flat", "icon", "convert-lowercase", 3, "click", "togglable", "svgIcon"]],
  template: function PDFViewerSearchComponent_Template(rf, ctx) {
    if (rf & 1) {
      const _r1 = i0.ɵɵgetCurrentView();
      i0.ɵɵelementStart(0, "kendo-textbox", 2, 0);
      i0.ɵɵlistener("valueChange", function PDFViewerSearchComponent_Template_kendo_textbox_valueChange_0_listener($event) {
        i0.ɵɵrestoreView(_r1);
        ctx.value = $event;
        return i0.ɵɵresetView(ctx.searchChange.emit({
          text: $event,
          matchCase: ctx.matchCase
        }));
      })("keydown.shift.tab", function PDFViewerSearchComponent_Template_kendo_textbox_keydown_shift_tab_0_listener($event) {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onShiftTab($event));
      });
      i0.ɵɵtemplate(2, PDFViewerSearchComponent_ng_template_2_Template, 1, 3, "ng-template", 3);
      i0.ɵɵelementEnd();
      i0.ɵɵelementStart(3, "span", 4)(4, "span");
      i0.ɵɵtext(5);
      i0.ɵɵelementEnd();
      i0.ɵɵtext(6);
      i0.ɵɵelementStart(7, "span");
      i0.ɵɵtext(8);
      i0.ɵɵelementEnd()();
      i0.ɵɵelementStart(9, "button", 5);
      i0.ɵɵlistener("click", function PDFViewerSearchComponent_Template_button_click_9_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.prevMatch.emit());
      });
      i0.ɵɵelementEnd();
      i0.ɵɵelementStart(10, "button", 6);
      i0.ɵɵlistener("click", function PDFViewerSearchComponent_Template_button_click_10_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.nextMatch.emit());
      });
      i0.ɵɵelementEnd();
      i0.ɵɵelementStart(11, "button", 7, 1);
      i0.ɵɵlistener("click", function PDFViewerSearchComponent_Template_button_click_11_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.close.emit());
      })("keydown.tab", function PDFViewerSearchComponent_Template_button_keydown_tab_11_listener($event) {
        i0.ɵɵrestoreView(_r1);
        const textbox_r4 = i0.ɵɵreference(1);
        $event.preventDefault();
        return i0.ɵɵresetView(textbox_r4.focus());
      });
      i0.ɵɵelementEnd();
    }
    if (rf & 2) {
      i0.ɵɵproperty("placeholder", ctx.messageFor("searchInputPlaceholder"))("value", ctx.value);
      i0.ɵɵadvance(5);
      i0.ɵɵtextInterpolate(ctx.currentMatch);
      i0.ɵɵadvance();
      i0.ɵɵtextInterpolate1(" ", ctx.messageFor("searchMatchesOf"), " ");
      i0.ɵɵadvance(2);
      i0.ɵɵtextInterpolate(ctx.matches);
      i0.ɵɵadvance();
      i0.ɵɵproperty("disabled", ctx.matches === 0)("svgIcon", ctx.arrowUpIcon);
      i0.ɵɵattribute("title", ctx.messageFor("searchPreviousMatchTitle"));
      i0.ɵɵadvance();
      i0.ɵɵproperty("disabled", ctx.matches === 0)("svgIcon", ctx.arrowDownIcon);
      i0.ɵɵattribute("title", ctx.messageFor("searchNextMatchTitle"));
      i0.ɵɵadvance();
      i0.ɵɵproperty("svgIcon", ctx.xIcon);
      i0.ɵɵattribute("title", ctx.messageFor("searchCloseTitle"));
    }
  },
  dependencies: [i2.TextBoxComponent, i5.ButtonComponent, i2.TextBoxSuffixTemplateDirective],
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PDFViewerSearchComponent, [{
    type: Component,
    args: [{
      selector: '[kendoPDFViewerSearch]',
      template: `
        <kendo-textbox
            #textbox
            [placeholder]="messageFor('searchInputPlaceholder')"
            [value]="value"
            (valueChange)="value = $event; searchChange.emit({text: $event, matchCase})"
            (keydown.shift.tab)="onShiftTab($event)">
            <ng-template kendoTextBoxSuffixTemplate>
                <button
                    type="button"
                    kendoButton
                    [attr.title]="messageFor('searchMatchCaseTitle')"
                    fillMode="flat"
                    [togglable]="true"
                    (click)="matchCase = !matchCase; searchChange.emit({text: value, matchCase})"
                    icon="convert-lowercase"
                    [svgIcon]="convertLowercaseIcon"></button>
            </ng-template>
        </kendo-textbox>
        <span class="k-search-matches">
            <span>{{currentMatch}}</span> {{messageFor('searchMatchesOf')}} <span>{{matches}}</span>
        </span>
        <button
            type="button"
            kendoButton
            [attr.title]="messageFor('searchPreviousMatchTitle')"
            fillMode="flat"
            [disabled]="matches === 0"
            (click)="prevMatch.emit()"
            icon='arrow-up'
            [svgIcon]="arrowUpIcon"></button>
        <button
            type="button"
            kendoButton
            [attr.title]="messageFor('searchNextMatchTitle')"
            fillMode="flat"
            [disabled]="matches === 0"
            (click)="nextMatch.emit()"
            icon='arrow-down'
            [svgIcon]="arrowDownIcon"></button>
        <button
            type="button"
            #closeButton
            kendoButton
            [attr.title]="messageFor('searchCloseTitle')"
            fillMode="flat"
            (click)="close.emit()"
            (keydown.tab)="$event.preventDefault(); textbox.focus();"
            icon='x'
            [svgIcon]="xIcon"></button>
    `
    }]
  }], function () {
    return [{
      type: i1$1.LocalizationService
    }];
  }, {
    textbox: [{
      type: ViewChild,
      args: ['textbox']
    }],
    closeButton: [{
      type: ViewChild,
      args: ['closeButton', {
        read: ElementRef
      }]
    }],
    ariaRole: [{
      type: HostBinding,
      args: ['attr.role']
    }],
    onEscape: [{
      type: HostListener,
      args: ['keydown.escape']
    }],
    matches: [{
      type: Input
    }],
    currentMatch: [{
      type: Input
    }],
    searchChange: [{
      type: Output
    }],
    prevMatch: [{
      type: Output
    }],
    nextMatch: [{
      type: Output
    }],
    close: [{
      type: Output
    }]
  });
})();

/**
 * @hidden
 */
class Messages extends ComponentMessages {}
Messages.ɵfac = /* @__PURE__ */(() => {
  let ɵMessages_BaseFactory;
  return function Messages_Factory(t) {
    return (ɵMessages_BaseFactory || (ɵMessages_BaseFactory = i0.ɵɵgetInheritedFactory(Messages)))(t || Messages);
  };
})();
Messages.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
  type: Messages,
  selectors: [["kendo-pdfviewer-messages-base"]],
  inputs: {
    zoomInTitle: "zoomInTitle",
    zoomOutTitle: "zoomOutTitle",
    selectionTitle: "selectionTitle",
    panningTitle: "panningTitle",
    searchTitle: "searchTitle",
    openTitle: "openTitle",
    downloadTitle: "downloadTitle",
    printTitle: "printTitle",
    pagerInputLabel: "pagerInputLabel",
    pagerInputTitle: "pagerInputTitle",
    pagerFirstPage: "pagerFirstPage",
    pagerPreviousPage: "pagerPreviousPage",
    pagerNextPage: "pagerNextPage",
    pagerLastPage: "pagerLastPage",
    pagerOf: "pagerOf",
    pagerPage: "pagerPage",
    fitToPage: "fitToPage",
    fitToWidth: "fitToWidth",
    searchInputPlaceholder: "searchInputPlaceholder",
    searchMatchesOf: "searchMatchesOf",
    searchPreviousMatchTitle: "searchPreviousMatchTitle",
    searchNextMatchTitle: "searchNextMatchTitle",
    searchCloseTitle: "searchCloseTitle",
    searchMatchCaseTitle: "searchMatchCaseTitle",
    zoomInputPlaceholder: "zoomInputPlaceholder"
  },
  features: [i0.ɵɵInheritDefinitionFeature]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Messages, [{
    type: Directive,
    args: [{
      // eslint-disable-next-line @angular-eslint/directive-selector
      selector: 'kendo-pdfviewer-messages-base'
    }]
  }], null, {
    zoomInTitle: [{
      type: Input
    }],
    zoomOutTitle: [{
      type: Input
    }],
    selectionTitle: [{
      type: Input
    }],
    panningTitle: [{
      type: Input
    }],
    searchTitle: [{
      type: Input
    }],
    openTitle: [{
      type: Input
    }],
    downloadTitle: [{
      type: Input
    }],
    printTitle: [{
      type: Input
    }],
    pagerInputLabel: [{
      type: Input
    }],
    pagerInputTitle: [{
      type: Input
    }],
    pagerFirstPage: [{
      type: Input
    }],
    pagerPreviousPage: [{
      type: Input
    }],
    pagerNextPage: [{
      type: Input
    }],
    pagerLastPage: [{
      type: Input
    }],
    pagerOf: [{
      type: Input
    }],
    pagerPage: [{
      type: Input
    }],
    fitToPage: [{
      type: Input
    }],
    fitToWidth: [{
      type: Input
    }],
    searchInputPlaceholder: [{
      type: Input
    }],
    searchMatchesOf: [{
      type: Input
    }],
    searchPreviousMatchTitle: [{
      type: Input
    }],
    searchNextMatchTitle: [{
      type: Input
    }],
    searchCloseTitle: [{
      type: Input
    }],
    searchMatchCaseTitle: [{
      type: Input
    }],
    zoomInputPlaceholder: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class LocalizedPDFViewerMessagesDirective extends Messages {
  constructor(service) {
    super();
    this.service = service;
  }
}
LocalizedPDFViewerMessagesDirective.ɵfac = function LocalizedPDFViewerMessagesDirective_Factory(t) {
  return new (t || LocalizedPDFViewerMessagesDirective)(i0.ɵɵdirectiveInject(i1$1.LocalizationService));
};
LocalizedPDFViewerMessagesDirective.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
  type: LocalizedPDFViewerMessagesDirective,
  selectors: [["", "kendoPDFViewerLocalizedMessages", ""]],
  features: [i0.ɵɵProvidersFeature([{
    provide: Messages,
    useExisting: forwardRef(() => LocalizedPDFViewerMessagesDirective)
  }]), i0.ɵɵInheritDefinitionFeature]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocalizedPDFViewerMessagesDirective, [{
    type: Directive,
    args: [{
      providers: [{
        provide: Messages,
        useExisting: forwardRef(() => LocalizedPDFViewerMessagesDirective)
      }],
      selector: '[kendoPDFViewerLocalizedMessages]'
    }]
  }], function () {
    return [{
      type: i1$1.LocalizationService
    }];
  }, null);
})();
let counter = 0;
/**
 * Represents the [Kendo UI PDFViewer component for Angular]({% slug overview_pdfviewer %}).
 */
class PDFViewerComponent {
  constructor(ngZone, renderer, localizationService) {
    this.ngZone = ngZone;
    this.renderer = renderer;
    this.localizationService = localizationService;
    this.hostClass = true;
    /**
     * Represents the tools collection rendered in the toolbar.
     * @default ['pager', 'spacer', 'zoomInOut', 'zoom', 'selection', 'spacer', 'search', 'open', 'download', 'print' ]
     */
    this.tools = ['pager', 'spacer', 'zoomInOut', 'zoom', 'selection', 'spacer', 'search', 'open', 'download', 'print'];
    /**
     * The configuration of the internal Loader component.
     * @default { size: 'large', type: 'pulsing', themeColor: 'primary' }
     */
    this.loaderSettings = {
      size: 'large',
      type: 'pulsing',
      themeColor: 'primary'
    };
    /**
     * The name of the file that will be saved when the end user clicks the Download tool.
     * @default 'Download'
     */
    this.saveFileName = 'Download';
    /**
     * Represents the options for saving the file when the end user clicks the Download tool.
     * @default {}
     */
    this.saveOptions = {};
    /**
     * The initial zoom level of the PDF document.
     *
     * @default 1.25
     */
    this.zoom = INITIAL_ZOOM_LEVEL;
    /**
     * Represents the step the zoom level will be changed with when using the ZoomIn and ZoomOut tools.
     * @default 0.25
     */
    this.zoomRate = 0.25;
    /**
     * Represents minimum allowed zoom value.
     * @default 0.5
     */
    this.minZoom = 0.5;
    /**
     * Represents maximum allowed zoom value.
     * @default 4
     */
    this.maxZoom = 4;
    /**
     * The event fired when a PDF document is successfully loaded in the `PDFViewer` component.
     */
    this.load = new EventEmitter();
    /**
     * The event fired when an error occurs during the loading or downloading of the PDF document.
     */
    this.error = new EventEmitter();
    /**
     * The event fired when an error occurs during the loading of the PDF document.
     */
    this.download = new EventEmitter();
    /**
     * The event fired when the end user changes the page of the loaded PDF document.
     */
    this.pageChange = new EventEmitter();
    /**
     * The event fired when the end user changes the zoom level of the loaded PDF document.
     */
    this.zoomLevelChange = new EventEmitter();
    this.loading = false;
    this.searchActive = false;
    this.matchCase = false;
    this.currentMatches = [];
    /**
     * @hidden
     */
    this.showLicenseWatermark = false;
    this.skip = 0;
    this.zoomOptionsData = zoomOptionsData;
    this.zoomLevel = this.zoomOptionsData[5];
    this._pdfContext = null;
    this._enabedSelection = false;
    this.subs = new Subscription();
    this.scrollListener = e => {
      const nextPage = currentPage(e.target.parentNode);
      if (nextPage !== this.skip) {
        this.ngZone.run(() => {
          hasObservers(this.pageChange) && this.pageChange.emit({
            previousPage: this.currentPage,
            currentPage: nextPage + 1
          });
          this.skip = nextPage;
        });
      }
    };
    const isValid = validatePackage(packageMetadata);
    this.showLicenseWatermark = shouldShowValidationUI(isValid);
    this.direction = localizationService.rtl ? 'rtl' : 'ltr';
    counter++;
  }
  /**
   * Returns the current page number of the loaded PDF document.
   */
  get currentPage() {
    const isDocumentLoaded = this.pagesContainer && this.pdfContext?.pdfDoc;
    return isDocumentLoaded ? this.skip + 1 : null;
  }
  set pdfContext(doc) {
    this._pdfContext = doc;
  }
  /**
   * Exposes the currently loaded `PDF.js` document, its pages, and their DOM element wrapper.
   */
  get pdfContext() {
    return this._pdfContext;
  }
  /**
   * @hidden
   */
  get activePageId() {
    if (!this.pdfContext?.pdfPages.length) {
      return;
    }
    return this.pagesContainer.nativeElement.querySelectorAll('.k-page')[this.skip]?.getAttribute('id');
  }
  /**
   * @hidden
   */
  get pagesContainerId() {
    if (!this.pdfContext?.pdfPages.length) {
      return;
    }
    return `k-pdfviewer-${counter}-pages-container`;
  }
  /**
   * @hidden
   */
  set enabledSelection(value) {
    if (this.pdfScroller) {
      value ? this.pdfScroller.disablePanEventsTracking() : this.pdfScroller.enablePanEventsTracking();
    }
    this._enabedSelection = value;
  }
  /**
   * @hidden
   */
  get enabledSelection() {
    return this._enabedSelection;
  }
  ngOnInit() {
    this.subs.add(this.localizationService.changes.subscribe(({
      rtl
    }) => this.direction = rtl ? 'rtl' : 'ltr'));
  }
  ngAfterViewInit() {
    const pagesContainer = this.pagesContainer.nativeElement.parentNode;
    this.ngZone.runOutsideAngular(() => this.subs.add(this.renderer.listen(pagesContainer, 'scroll', this.scrollListener)));
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
    removeChildren(this.pagesContainer.nativeElement);
    if (this.pdfScroller) {
      this.pdfScroller.destroy();
      this.pdfScroller = null;
    }
    if (this.searchService) {
      this.searchService.destroy();
      this.searchService = null;
    }
    this.pdfContext = null;
  }
  ngOnChanges(changes) {
    const newBase64Data = changes['data'] && this.data;
    const newUrl = changes['url'] && this.url;
    const newArrayBuffer = changes['arrayBuffer'] && this.arrayBuffer;
    const newTypedArray = changes['typedArray'] && this.typedArray;
    this.verifySettings(changes);
    const source = newUrl ? 'url' : newArrayBuffer ? 'arrayBuffer' : newBase64Data ? 'data' : 'typedArray';
    if (newUrl || newArrayBuffer || newBase64Data || newTypedArray) {
      this.loadPdf(source, this[source]);
    }
    if (changes['zoom']) {
      this.setZoomLevel(changes['zoom'].currentValue);
    }
  }
  /**
   * Scrolls the PDFViewer document to the specified page number, treating 0 as the first page.
   */
  scrollToPage(page) {
    scrollToPage(this.pagesContainer.nativeElement, page);
  }
  /**
   * @hidden
   */
  onFileSelect(e) {
    this.loadPdf('data', e);
  }
  /**
   * @hidden
   */
  onFileSelectError(e) {
    hasObservers(this.error) && this.error.emit({
      error: e,
      context: this.pdfContext
    });
  }
  /**
   * @hidden
   */
  onPageChange(e) {
    this.scrollToPage(e.skip);
    hasObservers(this.pageChange) && this.pageChange.emit({
      previousPage: this.skip + 1,
      currentPage: e.skip + 1
    });
    this.skip = e.skip;
  }
  /**
   * @hidden
   */
  onDownload() {
    this.ngZone.runOutsideAngular(() => this.pdfContext.pdfDoc.getData().then(data => new Blob([data], {
      type: 'application/pdf'
    })).then(blob => {
      const downloadEvent = new PDFViewerDownloadEvent(blob, this.saveFileName, this.saveOptions, this.pdfContext);
      this.ngZone.run(() => this.download.emit(downloadEvent));
      if (!downloadEvent.isDefaultPrevented()) {
        saveAs(blob, this.saveFileName, this.saveOptions);
      }
    }).catch(reason => {
      if (hasObservers(this.error)) {
        this.ngZone.run(() => this.error.emit({
          error: reason,
          context: this.pdfContext
        }));
      }
    }));
  }
  /**
   * @hidden
   */
  onZoomIn() {
    const newZoom = Math.min(this.pdfContext.zoom + this.zoomRate, this.maxZoom);
    if (newZoom !== this.pdfContext.zoom && this.pdfContext.pdfDoc) {
      if (hasObservers(this.zoomLevelChange)) {
        this.zoomLevelChange.emit({
          previousZoomLevel: this.zoom,
          currentZoomLevel: newZoom
        });
      }
      this.zoom = this.pdfContext.zoom = newZoom;
      this.setZoomLevel(newZoom);
      this.ngZone.runOutsideAngular(() => this.reloadDocument());
    }
  }
  /**
   * @hidden
   */
  onZoomOut() {
    const newZoom = Math.max(this.pdfContext.zoom - this.zoomRate, this.minZoom);
    if (newZoom !== this.pdfContext.zoom && this.pdfContext.pdfDoc) {
      if (hasObservers(this.zoomLevelChange)) {
        this.zoomLevelChange.emit({
          previousZoomLevel: this.zoom,
          currentZoomLevel: newZoom
        });
      }
      this.zoom = this.pdfContext.zoom = newZoom;
      this.setZoomLevel(newZoom);
      this.ngZone.runOutsideAngular(() => this.reloadDocument());
    }
  }
  /**
   * @hidden
   */
  onZoomLevelChange(zoomLevel) {
    if (typeof zoomLevel === 'string') {
      const parsedValue = parseFloat(zoomLevel);
      zoomLevel = {
        value: Number.isNaN(parsedValue) ? 1 : parsedValue / 100
      };
    }
    if (!zoomLevel) {
      zoomLevel = {
        value: 1
      };
    }
    let newZoom = calculateZoomLevel(zoomLevel.value, zoomLevel.type, this.pdfContext.zoom, this.pagesContainer.nativeElement);
    newZoom = Math.round(newZoom * 100) / 100;
    if (hasObservers(this.zoomLevelChange)) {
      this.zoomLevelChange.emit({
        previousZoomLevel: this.zoom,
        currentZoomLevel: newZoom
      });
    }
    this.zoom = this.pdfContext.zoom = newZoom;
    this.setZoomLevel(newZoom);
    this.ngZone.runOutsideAngular(() => this.reloadDocument());
  }
  /**
   * @hidden
   */
  onSearch() {
    this.searchActive = true;
    this.ngZone.runOutsideAngular(() => {
      const pagesTextLayers = Array.from(this.pagesContainer.nativeElement.querySelectorAll('.k-text-layer'));
      this.searchService = new SearchService({
        textContainers: pagesTextLayers || [],
        highlightClass: 'k-search-highlight',
        highlightMarkClass: 'k-search-highlight-mark',
        charClass: 'k-text-char'
      });
    });
  }
  /**
   * @hidden
   */
  onPrint() {
    this.loading = true;
    const onError = e => {
      this.loading = false;
      if (hasObservers(this.error)) {
        this.error.emit(e);
      }
      ;
    };
    const onDone = () => {
      this.loading = false;
    };
    print(this.pdfContext.pdfPages, onDone, onError);
  }
  /**
   * @hidden
   */
  onSearchChange(e) {
    this.currentMatches = this.searchService.search({
      ...e
    });
    this.currentMatch = this.currentMatches.length ? 1 : 0;
  }
  /**
   * @hidden
   */
  closeSearch(toolbar) {
    this.searchActive = false;
    this.searchService.destroy();
    this.currentMatches = [];
    this.currentMatch = null;
    toolbar.focus();
  }
  /**
   * @hidden
   */
  onNextMatch() {
    goToNextSearchMatch({
      search: this.searchService,
      scroller: this.pdfScroller
    });
    this.currentMatch = this.currentMatch + 1 > this.currentMatches.length ? 1 : this.currentMatch + 1;
  }
  /**
   * @hidden
   */
  onPreviousMatch() {
    goToPreviousSearchMatch({
      search: this.searchService,
      scroller: this.pdfScroller
    });
    this.currentMatch = this.currentMatch - 1 < 1 ? this.currentMatches.length : this.currentMatch - 1;
  }
  loadPdf(dataType, data) {
    this.loading = true;
    this.ngZone.runOutsideAngular(() => setTimeout(() => {
      removeChildren(this.pagesContainer.nativeElement);
      loadPDF({
        [dataType]: data,
        dom: this.pagesContainer.nativeElement,
        zoom: this.pdfContext?.zoom || this.zoom,
        done: e => {
          this.ngZone.run(() => {
            this.loading = false;
            this.pdfContext = {
              ...e,
              element: this.pagesContainer.nativeElement
            };
          });
          if (this.pdfScroller) {
            this.pdfScroller.destroy();
          }
          this.pdfScroller = this.pdfScroller = new Scroller(this.pagesContainer.nativeElement.parentNode, {
            filter: '.k-page',
            events: {}
          });
          this.pdfScroller.enablePanEventsTracking();
          this.assignPageIds();
          if (hasObservers(this.load)) {
            this.ngZone.run(() => this.load.emit({
              context: this.pdfContext
            }));
          }
        },
        error: e => {
          this.ngZone.run(() => this.loading = false);
          this.pdfContext = null;
          if (hasObservers(this.error)) {
            this.ngZone.run(() => this.error.emit({
              error: e,
              context: this.pdfContext
            }));
          }
        }
      });
    }));
  }
  reloadDocument() {
    if (this.pagesContainer?.nativeElement) {
      this.ngZone.run(() => this.loading = true);
      removeChildren(this.pagesContainer.nativeElement);
      reloadDocument({
        pdfDoc: this.pdfContext?.pdfDoc,
        zoom: this.zoom,
        dom: this.pagesContainer.nativeElement,
        done: pdfPages => {
          this.pdfContext.pdfPages = pdfPages;
          this.ngZone.run(() => this.loading = false);
          this.assignPageIds();
        },
        error: e => {
          if (this.pdfScroller) {
            this.pdfScroller.destroy();
            this.pdfScroller = null;
          }
          this.pdfContext = null;
          if (hasObservers(this.error)) {
            this.ngZone.run(() => this.error.emit({
              error: e,
              context: this.pdfContext
            }));
          }
        }
      });
    }
  }
  assignPageIds() {
    this.pagesContainer.nativeElement.querySelectorAll('.k-page').forEach((pageElement, index) => {
      const id = `k-pdfviewer-${counter}-page-${index + 1}`;
      this.renderer.setAttribute(pageElement, 'id', id);
    });
  }
  setZoomLevel(zoom) {
    const option = this.zoomOptionsData.find(item => !item.type && item.value === zoom);
    this.zoomLevel = option || {
      value: zoom,
      displayValue: `${Math.round(zoom * 100)}%`,
      text: `${Math.round(zoom * 100)}%`
    };
  }
  verifySettings(changes) {
    const sourceTypes = ['url', 'arrayBuffer', 'data', 'typedArray'];
    const setTypes = sourceTypes.map(item => this.isSet(changes, item)).filter(item => item);
    if (isDevMode() && setTypes.length > 1) {
      console.warn(`
                Setting more than one source of data simultaneously is not supported.
                Only one of 'url', 'arrayBuffer', 'data', or 'typedArray' can be set.
            `);
    }
  }
  isSet(changes, source) {
    return this[source] || changes[source] && changes[source].currentValue;
  }
}
PDFViewerComponent.ɵfac = function PDFViewerComponent_Factory(t) {
  return new (t || PDFViewerComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1$1.LocalizationService));
};
PDFViewerComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: PDFViewerComponent,
  selectors: [["kendo-pdfviewer"]],
  viewQuery: function PDFViewerComponent_Query(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵviewQuery(_c7, 5);
    }
    if (rf & 2) {
      let _t;
      i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.pagesContainer = _t.first);
    }
  },
  hostVars: 3,
  hostBindings: function PDFViewerComponent_HostBindings(rf, ctx) {
    if (rf & 2) {
      i0.ɵɵattribute("dir", ctx.direction);
      i0.ɵɵclassProp("k-pdf-viewer", ctx.hostClass);
    }
  },
  inputs: {
    tools: "tools",
    loaderSettings: "loaderSettings",
    saveFileName: "saveFileName",
    saveOptions: "saveOptions",
    url: "url",
    data: "data",
    arrayBuffer: "arrayBuffer",
    typedArray: "typedArray",
    zoom: "zoom",
    zoomRate: "zoomRate",
    minZoom: "minZoom",
    maxZoom: "maxZoom"
  },
  outputs: {
    load: "load",
    error: "error",
    download: "download",
    pageChange: "pageChange",
    zoomLevelChange: "zoomLevelChange"
  },
  exportAs: ["kendo-pdfviewer"],
  features: [i0.ɵɵProvidersFeature([LocalizationService, {
    provide: L10N_PREFIX,
    useValue: 'kendo.pdfviewer'
  }]), i0.ɵɵNgOnChangesFeature],
  decls: 11,
  vars: 21,
  consts: () => {
    let i18n_0;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The label for the first page button in the Pager
       * @meaning kendo.pdfviewer.pagerFirstPage
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_0 = goog.getMsg("Go to the first page");
      i18n_0 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_0;
    } else {
      i18n_0 = $localize`:kendo.pdfviewer.pagerFirstPage|The label for the first page button in the Pager:Go to the first page`;
    }
    let i18n_1;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The label for the previous page button in the Pager
       * @meaning kendo.pdfviewer.pagerPreviousPage
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_1 = goog.getMsg("Go to the previous page");
      i18n_1 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_1;
    } else {
      i18n_1 = $localize`:kendo.pdfviewer.pagerPreviousPage|The label for the previous page button in the Pager:Go to the previous page`;
    }
    let i18n_2;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The label for the next page button in the Pager
       * @meaning kendo.pdfviewer.pagerNextPage
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_2 = goog.getMsg("Go to the next page");
      i18n_2 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_2;
    } else {
      i18n_2 = $localize`:kendo.pdfviewer.pagerNextPage|The label for the next page button in the Pager:Go to the next page`;
    }
    let i18n_3;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The label for the last page button in the Pager
       * @meaning kendo.pdfviewer.pagerLastPage
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_3 = goog.getMsg("Go to the last page");
      i18n_3 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_3;
    } else {
      i18n_3 = $localize`:kendo.pdfviewer.pagerLastPage|The label for the last page button in the Pager:Go to the last page`;
    }
    let i18n_4;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text before the current page number in the Pager
       * @meaning kendo.pdfviewer.pagerPage
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_4 = goog.getMsg("Page");
      i18n_4 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_4;
    } else {
      i18n_4 = $localize`:kendo.pdfviewer.pagerPage|The text before the current page number in the Pager:Page`;
    }
    let i18n_5;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text before the total pages number in the Pager
       * @meaning kendo.pdfviewer.pagerOf
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_5 = goog.getMsg("of");
      i18n_5 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_5;
    } else {
      i18n_5 = $localize`:kendo.pdfviewer.pagerOf|The text before the total pages number in the Pager:of`;
    }
    let i18n_6;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The label of the pager input element
       * @meaning kendo.pdfviewer.pagerInputTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_6 = goog.getMsg("Page Number");
      i18n_6 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_6;
    } else {
      i18n_6 = $localize`:kendo.pdfviewer.pagerInputTitle|The label of the pager input element:Page Number`;
    }
    let i18n_7;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text of the aria-label attribute applied to the input element for entering the page number.
       * @meaning kendo.pdfviewer.pagerInputLabel
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_7 = goog.getMsg("Type a page number");
      i18n_7 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_7;
    } else {
      i18n_7 = $localize`:kendo.pdfviewer.pagerInputLabel|The text of the aria-label attribute applied to the input element for entering the page number.:Type a page number`;
    }
    let i18n_8;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The ZoomIn tool title and label.
       * @meaning kendo.pdfviewer.zoomInTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_8 = goog.getMsg("Zoom in");
      i18n_8 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_8;
    } else {
      i18n_8 = $localize`:kendo.pdfviewer.zoomInTitle|The ZoomIn tool title and label.:Zoom in`;
    }
    let i18n_9;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The ZoomOut tool title and label.
       * @meaning kendo.pdfviewer.zoomOutTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_9 = goog.getMsg("Zoom out");
      i18n_9 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_9;
    } else {
      i18n_9 = $localize`:kendo.pdfviewer.zoomOutTitle|The ZoomOut tool title and label.:Zoom out`;
    }
    let i18n_10;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The Selection tool title and label.
       * @meaning kendo.pdfviewer.selectionTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_10 = goog.getMsg("Enable selection");
      i18n_10 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_10;
    } else {
      i18n_10 = $localize`:kendo.pdfviewer.selectionTitle|The Selection tool title and label.:Enable selection`;
    }
    let i18n_11;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The Panning tool title and label.
       * @meaning kendo.pdfviewer.panningTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_11 = goog.getMsg("Enable panning");
      i18n_11 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_11;
    } else {
      i18n_11 = $localize`:kendo.pdfviewer.panningTitle|The Panning tool title and label.:Enable panning`;
    }
    let i18n_12;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The Search tool title and label.
       * @meaning kendo.pdfviewer.searchTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_12 = goog.getMsg("Search");
      i18n_12 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_12;
    } else {
      i18n_12 = $localize`:kendo.pdfviewer.searchTitle|The Search tool title and label.:Search`;
    }
    let i18n_13;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The Open tool title and label.
       * @meaning kendo.pdfviewer.openTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_13 = goog.getMsg("Open");
      i18n_13 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_13;
    } else {
      i18n_13 = $localize`:kendo.pdfviewer.openTitle|The Open tool title and label.:Open`;
    }
    let i18n_14;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The Download tool title and label.
       * @meaning kendo.pdfviewer.downloadTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_14 = goog.getMsg("Download");
      i18n_14 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_14;
    } else {
      i18n_14 = $localize`:kendo.pdfviewer.downloadTitle|The Download tool title and label.:Download`;
    }
    let i18n_15;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The Print tool title and label.
       * @meaning kendo.pdfviewer.printTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_15 = goog.getMsg("Print");
      i18n_15 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_15;
    } else {
      i18n_15 = $localize`:kendo.pdfviewer.printTitle|The Print tool title and label.:Print`;
    }
    let i18n_16;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text for the zoom level chooser Fit to page option.
       * @meaning kendo.pdfviewer.fitToPage
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_16 = goog.getMsg("Fit to page");
      i18n_16 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_16;
    } else {
      i18n_16 = $localize`:kendo.pdfviewer.fitToPage|The text for the zoom level chooser Fit to page option.:Fit to page`;
    }
    let i18n_17;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text for the zoom level chooser Fit to width option.
       * @meaning kendo.pdfviewer.fitToWidth
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_17 = goog.getMsg("Fit to width");
      i18n_17 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_17;
    } else {
      i18n_17 = $localize`:kendo.pdfviewer.fitToWidth|The text for the zoom level chooser Fit to width option.:Fit to width`;
    }
    let i18n_18;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text for the search input placeholder.
       * @meaning kendo.pdfviewer.searchInputPlaceholder
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_18 = goog.getMsg("Search");
      i18n_18 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_18;
    } else {
      i18n_18 = $localize`:kendo.pdfviewer.searchInputPlaceholder|The text for the search input placeholder.:Search`;
    }
    let i18n_19;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text before the total number of matches in the Search tool.
       * @meaning kendo.pdfviewer.searchMatchesOf
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_19 = goog.getMsg("of");
      i18n_19 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_19;
    } else {
      i18n_19 = $localize`:kendo.pdfviewer.searchMatchesOf|The text before the total number of matches in the Search tool.:of`;
    }
    let i18n_20;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The title of the Search tool previous match button.
       * @meaning kendo.pdfviewer.searchPreviousMatchTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_20 = goog.getMsg("Previous match");
      i18n_20 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_20;
    } else {
      i18n_20 = $localize`:kendo.pdfviewer.searchPreviousMatchTitle|The title of the Search tool previous match button.:Previous match`;
    }
    let i18n_21;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The title of the Search tool next match button.
       * @meaning kendo.pdfviewer.searchNextMatchTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_21 = goog.getMsg("Next match");
      i18n_21 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_21;
    } else {
      i18n_21 = $localize`:kendo.pdfviewer.searchNextMatchTitle|The title of the Search tool next match button.:Next match`;
    }
    let i18n_22;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The title of the Search tool close button.
       * @meaning kendo.pdfviewer.searchCloseTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_22 = goog.getMsg("Close");
      i18n_22 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_22;
    } else {
      i18n_22 = $localize`:kendo.pdfviewer.searchCloseTitle|The title of the Search tool close button.:Close`;
    }
    let i18n_23;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The title of the Search tool match case button.
       * @meaning kendo.pdfviewer.searchMatchCaseTitle
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_23 = goog.getMsg("Match case");
      i18n_23 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_23;
    } else {
      i18n_23 = $localize`:kendo.pdfviewer.searchMatchCaseTitle|The title of the Search tool match case button.:Match case`;
    }
    let i18n_24;
    if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
      /**
       * @desc The text for the zoom tool input placeholder.
       * @meaning kendo.pdfviewer.zoomInputPlaceholder
       */
      const MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_24 = goog.getMsg("Choose zoom level");
      i18n_24 = MSG__USERS_BSCOTT28_CODE_2021_CASI_NODEJSWEB_ENVIRONER_NG_NODE_MODULES__PROGRESS_KENDO_ANGULAR_PDFVIEWER_FESM2020_PROGRESS_KENDO_ANGULAR_PDFVIEWER_MJS_24;
    } else {
      i18n_24 = $localize`:kendo.pdfviewer.zoomInputPlaceholder|The text for the zoom tool input placeholder.:Choose zoom level`;
    }
    return [["toolbar", ""], ["pagesContainer", ""], ["kendoPDFViewerLocalizedMessages", "", "pagerFirstPage", i18n_0, "pagerPreviousPage", i18n_1, "pagerNextPage", i18n_2, "pagerLastPage", i18n_3, "pagerPage", i18n_4, "pagerOf", i18n_5, "pagerInputTitle", i18n_6, "pagerInputLabel", i18n_7, "zoomInTitle", i18n_8, "zoomOutTitle", i18n_9, "selectionTitle", i18n_10, "panningTitle", i18n_11, "searchTitle", i18n_12, "openTitle", i18n_13, "downloadTitle", i18n_14, "printTitle", i18n_15, "fitToPage", i18n_16, "fitToWidth", i18n_17, "searchInputPlaceholder", i18n_18, "searchMatchesOf", i18n_19, "searchPreviousMatchTitle", i18n_20, "searchNextMatchTitle", i18n_21, "searchCloseTitle", i18n_22, "searchMatchCaseTitle", i18n_23, "zoomInputPlaceholder", i18n_24], ["kendoPDFViewerLoader", "", 3, "settings", 4, "ngIf"], ["kendoPDFViewerToolbar", "", "role", "toolbar", 3, "pageChange", "fileSelect", "fileSelectStart", "fileSelectError", "download", "panningEnabled", "selectionEnabled", "zoomIn", "zoomOut", "zoomLevelChange", "print", "search", "tools", "skip", "total", "pageSize", "zoomInDisabled", "zoomOutDisabled", "zoomOptionsData", "zoomLevelChooserValue", "disabledTools"], ["tabindex", "0", 1, "k-canvas", "k-pdf-viewer-canvas", "k-pos-relative", "k-overflow-auto"], ["kendoPDFViewerSearch", "", "class", "k-search-panel k-pos-sticky k-top-center", 3, "currentMatch", "matches", "searchChange", "prevMatch", "nextMatch", "close", 4, "ngIf"], [1, "k-pdf-viewer-pages"], [1, "k-page"], [1, "k-text-layer"], ["kendoWatermarkOverlay", "", 4, "ngIf"], ["kendoPDFViewerLoader", "", 3, "settings"], ["kendoPDFViewerSearch", "", 1, "k-search-panel", "k-pos-sticky", "k-top-center", 3, "searchChange", "prevMatch", "nextMatch", "close", "currentMatch", "matches"], ["kendoWatermarkOverlay", ""]];
  },
  template: function PDFViewerComponent_Template(rf, ctx) {
    if (rf & 1) {
      const _r1 = i0.ɵɵgetCurrentView();
      i0.ɵɵelementContainer(0, 2);
      i0.ɵɵtemplate(1, PDFViewerComponent_div_1_Template, 1, 1, "div", 3);
      i0.ɵɵelementStart(2, "div", 4, 0);
      i0.ɵɵlistener("pageChange", function PDFViewerComponent_Template_div_pageChange_2_listener($event) {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onPageChange($event));
      })("fileSelect", function PDFViewerComponent_Template_div_fileSelect_2_listener($event) {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onFileSelect($event));
      })("fileSelectStart", function PDFViewerComponent_Template_div_fileSelectStart_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.loading = true);
      })("fileSelectError", function PDFViewerComponent_Template_div_fileSelectError_2_listener($event) {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onFileSelectError($event));
      })("download", function PDFViewerComponent_Template_div_download_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onDownload());
      })("panningEnabled", function PDFViewerComponent_Template_div_panningEnabled_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.enabledSelection = false);
      })("selectionEnabled", function PDFViewerComponent_Template_div_selectionEnabled_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.enabledSelection = true);
      })("zoomIn", function PDFViewerComponent_Template_div_zoomIn_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onZoomIn());
      })("zoomOut", function PDFViewerComponent_Template_div_zoomOut_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onZoomOut());
      })("zoomLevelChange", function PDFViewerComponent_Template_div_zoomLevelChange_2_listener($event) {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onZoomLevelChange($event));
      })("print", function PDFViewerComponent_Template_div_print_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onPrint());
      })("search", function PDFViewerComponent_Template_div_search_2_listener() {
        i0.ɵɵrestoreView(_r1);
        return i0.ɵɵresetView(ctx.onSearch());
      });
      i0.ɵɵelementEnd();
      i0.ɵɵelementStart(4, "div", 5);
      i0.ɵɵtemplate(5, PDFViewerComponent_div_5_Template, 1, 2, "div", 6);
      i0.ɵɵelementStart(6, "div", 7, 1)(8, "div", 8);
      i0.ɵɵelement(9, "div", 9);
      i0.ɵɵelementEnd()()();
      i0.ɵɵtemplate(10, PDFViewerComponent_div_10_Template, 1, 0, "div", 10);
    }
    if (rf & 2) {
      i0.ɵɵadvance();
      i0.ɵɵproperty("ngIf", ctx.loading);
      i0.ɵɵadvance();
      i0.ɵɵproperty("tools", ctx.tools)("skip", ctx.skip)("total", (ctx.pdfContext == null ? null : ctx.pdfContext.pdfPages.length) ? ctx.pdfContext == null ? null : ctx.pdfContext.pdfPages.length : 1)("pageSize", 1)("zoomInDisabled", ctx.zoom >= ctx.maxZoom || !(ctx.pdfContext == null ? null : ctx.pdfContext.pdfDoc))("zoomOutDisabled", ctx.zoom <= ctx.minZoom || !(ctx.pdfContext == null ? null : ctx.pdfContext.pdfDoc))("zoomOptionsData", ctx.zoomOptionsData)("zoomLevelChooserValue", ctx.zoomLevel)("disabledTools", !(ctx.pdfContext == null ? null : ctx.pdfContext.pdfDoc));
      i0.ɵɵattribute("aria-controls", ctx.pagesContainerId);
      i0.ɵɵadvance(2);
      i0.ɵɵclassProp("k-enable-text-select", ctx.enabledSelection)("k-enable-panning", !ctx.enabledSelection);
      i0.ɵɵattribute("aria-describedby", ctx.activePageId);
      i0.ɵɵadvance();
      i0.ɵɵproperty("ngIf", ctx.searchActive);
      i0.ɵɵadvance();
      i0.ɵɵstyleProp("--scale-factor", ctx.zoom);
      i0.ɵɵattribute("id", ctx.pagesContainerId);
      i0.ɵɵadvance(4);
      i0.ɵɵproperty("ngIf", ctx.showLicenseWatermark);
    }
  },
  dependencies: [LoaderComponent, ToolbarComponent, PDFViewerSearchComponent, i5$1.WatermarkOverlayComponent, LocalizedPDFViewerMessagesDirective, i7.NgIf],
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PDFViewerComponent, [{
    type: Component,
    args: [{
      exportAs: 'kendo-pdfviewer',
      selector: 'kendo-pdfviewer',
      providers: [LocalizationService, {
        provide: L10N_PREFIX,
        useValue: 'kendo.pdfviewer'
      }],
      template: `
        <ng-container kendoPDFViewerLocalizedMessages
            i18n-pagerFirstPage="kendo.pdfviewer.pagerFirstPage|The label for the first page button in the Pager"
            pagerFirstPage="Go to the first page"

            i18n-pagerPreviousPage="kendo.pdfviewer.pagerPreviousPage|The label for the previous page button in the Pager"
            pagerPreviousPage="Go to the previous page"

            i18n-pagerNextPage="kendo.pdfviewer.pagerNextPage|The label for the next page button in the Pager"
            pagerNextPage="Go to the next page"

            i18n-pagerLastPage="kendo.pdfviewer.pagerLastPage|The label for the last page button in the Pager"
            pagerLastPage="Go to the last page"

            i18n-pagerPage="kendo.pdfviewer.pagerPage|The text before the current page number in the Pager"
            pagerPage="Page"

            i18n-pagerOf="kendo.pdfviewer.pagerOf|The text before the total pages number in the Pager"
            pagerOf="of"

            i18n-pagerInputTitle="kendo.pdfviewer.pagerInputTitle|The label of the pager input element"
            pagerInputTitle="Page Number"

            i18n-pagerInputLabel="kendo.pdfviewer.pagerInputLabel|The text of the aria-label attribute applied to the input element for entering the page number."
            pagerInputLabel="Type a page number"
            
            i18n-zoomInTitle="kendo.pdfviewer.zoomInTitle|The ZoomIn tool title and label."
            zoomInTitle="Zoom in"
            
            i18n-zoomOutTitle="kendo.pdfviewer.zoomOutTitle|The ZoomOut tool title and label."
            zoomOutTitle="Zoom out"
            
            i18n-selectionTitle="kendo.pdfviewer.selectionTitle|The Selection tool title and label."
            selectionTitle="Enable selection"
            
            i18n-panningTitle="kendo.pdfviewer.panningTitle|The Panning tool title and label."
            panningTitle="Enable panning"
            
            i18n-searchTitle="kendo.pdfviewer.searchTitle|The Search tool title and label."
            searchTitle="Search"
            
            i18n-openTitle="kendo.pdfviewer.openTitle|The Open tool title and label."
            openTitle="Open"
            
            i18n-downloadTitle="kendo.pdfviewer.downloadTitle|The Download tool title and label."
            downloadTitle="Download"
            
            i18n-printTitle="kendo.pdfviewer.printTitle|The Print tool title and label."
            printTitle="Print"
            
            i18n-fitToPage="kendo.pdfviewer.fitToPage|The text for the zoom level chooser Fit to page option."
            fitToPage="Fit to page"
            
            i18n-fitToWidth="kendo.pdfviewer.fitToWidth|The text for the zoom level chooser Fit to width option."
            fitToWidth="Fit to width"
            
            i18n-searchInputPlaceholder="kendo.pdfviewer.searchInputPlaceholder|The text for the search input placeholder."
            searchInputPlaceholder="Search"
            
            i18n-searchMatchesOf="kendo.pdfviewer.searchMatchesOf|The text before the total number of matches in the Search tool."
            searchMatchesOf="of"
            
            i18n-searchPreviousMatchTitle="kendo.pdfviewer.searchPreviousMatchTitle|The title of the Search tool previous match button."
            searchPreviousMatchTitle="Previous match"
            
            i18n-searchNextMatchTitle="kendo.pdfviewer.searchNextMatchTitle|The title of the Search tool next match button."
            searchNextMatchTitle="Next match"
            
            i18n-searchCloseTitle="kendo.pdfviewer.searchCloseTitle|The title of the Search tool close button."
            searchCloseTitle="Close"
            
            i18n-searchMatchCaseTitle="kendo.pdfviewer.searchMatchCaseTitle|The title of the Search tool match case button."
            searchMatchCaseTitle="Match case"
            
            i18n-zoomInputPlaceholder="kendo.pdfviewer.zoomInputPlaceholder|The text for the zoom tool input placeholder."
            zoomInputPlaceholder="Choose zoom level">
        </ng-container>
        <div kendoPDFViewerLoader 
            *ngIf="loading"
            [settings]="loaderSettings"></div>
        <div kendoPDFViewerToolbar
            #toolbar
            role="toolbar"
            [attr.aria-controls]="pagesContainerId"
            [tools]="tools"
            [skip]="skip"
            [total]="pdfContext?.pdfPages.length ? pdfContext?.pdfPages.length : 1"
            [pageSize]="1"
            [zoomInDisabled]="zoom >= maxZoom || !pdfContext?.pdfDoc"
            [zoomOutDisabled]="zoom <= minZoom || !pdfContext?.pdfDoc"
            [zoomOptionsData]="zoomOptionsData"
            [zoomLevelChooserValue]="zoomLevel"
            [disabledTools]="!pdfContext?.pdfDoc"
            (pageChange)="onPageChange($event)"
            (fileSelect)="onFileSelect($event)"
            (fileSelectStart)="loading = true;"
            (fileSelectError)="onFileSelectError($event)"
            (download)="onDownload()"
            (panningEnabled)="enabledSelection = false;"
            (selectionEnabled)="enabledSelection = true;"
            (zoomIn)="onZoomIn()"
            (zoomOut)="onZoomOut()"
            (zoomLevelChange)="onZoomLevelChange($event)"
            (print)="onPrint()"
            (search)="onSearch()"></div>
        <div
            class="k-canvas k-pdf-viewer-canvas k-pos-relative k-overflow-auto"
            [class.k-enable-text-select]="enabledSelection"
            [class.k-enable-panning]="!enabledSelection"
            [attr.aria-describedby]="activePageId"
            tabindex="0">
            <div
                kendoPDFViewerSearch
                *ngIf="searchActive"
                [currentMatch]="currentMatch"
                [matches]="currentMatches.length"
                class="k-search-panel k-pos-sticky k-top-center"
                (searchChange)="onSearchChange($event)"
                (prevMatch)="onPreviousMatch()"
                (nextMatch)="onNextMatch()"
                (close)="closeSearch(toolbar)"></div>
            <div 
                #pagesContainer
                class="k-pdf-viewer-pages"
                [style.--scale-factor]="zoom"
                [attr.id]="pagesContainerId">
                <div class="k-page">
                    <div class="k-text-layer"></div>
                </div>
            </div>
        </div>

        <div kendoWatermarkOverlay *ngIf="showLicenseWatermark"></div>
    `
    }]
  }], function () {
    return [{
      type: i0.NgZone
    }, {
      type: i0.Renderer2
    }, {
      type: i1$1.LocalizationService
    }];
  }, {
    hostClass: [{
      type: HostBinding,
      args: ['class.k-pdf-viewer']
    }],
    direction: [{
      type: HostBinding,
      args: ['attr.dir']
    }],
    tools: [{
      type: Input
    }],
    loaderSettings: [{
      type: Input
    }],
    saveFileName: [{
      type: Input
    }],
    saveOptions: [{
      type: Input
    }],
    url: [{
      type: Input
    }],
    data: [{
      type: Input
    }],
    arrayBuffer: [{
      type: Input
    }],
    typedArray: [{
      type: Input
    }],
    zoom: [{
      type: Input
    }],
    zoomRate: [{
      type: Input
    }],
    minZoom: [{
      type: Input
    }],
    maxZoom: [{
      type: Input
    }],
    load: [{
      type: Output
    }],
    error: [{
      type: Output
    }],
    download: [{
      type: Output
    }],
    pageChange: [{
      type: Output
    }],
    zoomLevelChange: [{
      type: Output
    }],
    pagesContainer: [{
      type: ViewChild,
      args: ['pagesContainer']
    }]
  });
})();

/**
 * Custom component messages override default component messages
 * ([see example]({% slug globalization_pdfviewer %})).
 */
class PDFViewerCustomMessagesComponent extends Messages {
  constructor(service) {
    super();
    this.service = service;
  }
  get override() {
    return true;
  }
}
PDFViewerCustomMessagesComponent.ɵfac = function PDFViewerCustomMessagesComponent_Factory(t) {
  return new (t || PDFViewerCustomMessagesComponent)(i0.ɵɵdirectiveInject(i1$1.LocalizationService));
};
PDFViewerCustomMessagesComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: PDFViewerCustomMessagesComponent,
  selectors: [["kendo-pdfviewer-messages"]],
  features: [i0.ɵɵProvidersFeature([{
    provide: Messages,
    useExisting: forwardRef(() => PDFViewerCustomMessagesComponent)
  }]), i0.ɵɵInheritDefinitionFeature],
  decls: 0,
  vars: 0,
  template: function PDFViewerCustomMessagesComponent_Template(rf, ctx) {},
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PDFViewerCustomMessagesComponent, [{
    type: Component,
    args: [{
      providers: [{
        provide: Messages,
        useExisting: forwardRef(() => PDFViewerCustomMessagesComponent)
      }],
      selector: 'kendo-pdfviewer-messages',
      template: ``
    }]
  }], function () {
    return [{
      type: i1$1.LocalizationService
    }];
  }, null);
})();
const DIRECTIVES = [PDFViewerComponent, ToolbarComponent, LoaderComponent, PDFViewerCustomMessagesComponent, LocalizedPDFViewerMessagesDirective, ToolbarInputWrapperComponent, ToolbarFocusableDirective, ToolbarComboBoxDirective, PDFViewerSearchComponent];
const IMPORTS = [CommonModule, IconsModule, PagerModule, ButtonsModule, TextBoxModule, ComboBoxModule, LoaderModule, WatermarkModule];
/**
 * Represents the [NgModule](link:site.data.urls.angular['ngmoduleapi'])
 * definition for the PDFViewer component.
 */
class PDFViewerModule {}
PDFViewerModule.ɵfac = function PDFViewerModule_Factory(t) {
  return new (t || PDFViewerModule)();
};
PDFViewerModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
  type: PDFViewerModule
});
PDFViewerModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
  imports: [IMPORTS]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PDFViewerModule, [{
    type: NgModule,
    args: [{
      declarations: DIRECTIVES,
      exports: DIRECTIVES,
      imports: IMPORTS
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { LoaderComponent, LocalizedPDFViewerMessagesDirective, PDFViewerComponent, PDFViewerCustomMessagesComponent, PDFViewerDownloadEvent, PDFViewerModule, PDFViewerSearchComponent, ToolbarComboBoxDirective, ToolbarComponent, ToolbarFocusableDirective, ToolbarInputWrapperComponent };